import { Controller } from "@hotwired/stimulus"
import { useDebounce } from "stimulus-use"
import { MarkerClusterer, Cluster, AbstractAlgorithm } from "@googlemaps/markerclusterer"

// Custom clustering algorithm that groups markers with the exact same location
class ExactLocationAlgorithm extends AbstractAlgorithm {
  calculate({ markers }) {
    if (!markers || markers.length === 0) {
      return { clusters: [] }
    }

    const groups = new Map()

    markers.forEach((marker) => {
      const pos = marker.position
      if (!pos) return

      const key = `${pos.lat},${pos.lng}`

      if (!groups.has(key)) { groups.set(key, []) }
      groups.get(key).push(marker)
    })

    const clusters = Array.from(groups.values()).map((clusterMarkers) => {
      return new Cluster({ markers: clusterMarkers })
    })

    return { clusters }
  }
}

const MEDIUM_BREAKPOINT = 992

const MAX_ZOOM_BY_QUERY_TYPE = {
  "tourist_attraction": 15,
  "point_of_interest": 15,
  "establishment": 15,
  "premise": 15,
  "route": 15,
  "street_address": 15,
  "sublocality": 14,
  "neighborhood": 14,
  "locality": 13,
  "postal_code": 13,
  "administrative_area_level_2": 10,
  "administrative_area_level_1": 9,
  "country": 7,
}

export const PIN_COLORS = {
  "default": "#221644",
  "pinned": "#AE92FF",
  "hover": "#FFD71C",
}

const MARKER_BASE_STYLE = "filter: drop-shadow(0px 3px 4px rgba(0, 0, 0, 0.4)); transform: translateY(50%);"

const buildSvgMarker = (variant, circleColor) => {
  const parser = new DOMParser()
  const svgString = `
    <svg
      data-variant="${variant}"
      width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
      style="${MARKER_BASE_STYLE}"
    >
      <circle cx="12" cy="12" r="12" fill="white"/>
      <circle cx="12" cy="12" r="5" fill="${circleColor}"/>
    </svg>
  `
  return parser.parseFromString(svgString, "image/svg+xml").documentElement
}

// Trick to make a FontAwesome glyph render with a gradient (background-image) "color"
const GRADIENT_TEXT_STYLE = "background-clip: text; -webkit-background-clip: text; -webkit-text-fill-color: transparent; color: transparent;"

const buildIconMarker = (variant, iconBaseClass, { wrapperBg = "white", iconClass = "", iconStyle = "" } = {}) => {
  const wrapper = document.createElement("div")
  wrapper.dataset.variant = variant
  wrapper.style.cssText = `
    ${MARKER_BASE_STYLE}
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${wrapperBg};
  `
  const icon = document.createElement("i")
  icon.className = `${iconBaseClass} fas ${iconClass}`.trim()
  icon.style.cssText = `font-size: 12px; ${iconStyle}`
  wrapper.appendChild(icon)
  return wrapper
}

// Custom marker icon — variant: "default" | "pinned" | "bookmark" | "promotional"
export const markerIcon = (variant = "default") => {
  if (variant === "bookmark") {
    return buildIconMarker("bookmark", "fa-bookmark", { iconClass: "text-info" })
  }
  if (variant === "promotional") {
    // White pin with the badge-percent glyph rendered in the bg-offer-strong gradient
    // (the gradient becomes the icon's "color" via background-clip: text)
    return buildIconMarker("promotional", "fa-badge-percent", {
      iconClass: "bg-offer-strong",
      iconStyle: GRADIENT_TEXT_STYLE,
    })
  }
  const color = variant === "pinned" ? PIN_COLORS["pinned"] : PIN_COLORS["default"]
  return buildSvgMarker(variant, color)
}

/**
 *
 * This controller manages the product search functionality.
 * It intercepts changes made to the filters directly in the main form or in the subcomponents managed by the outlets.
 * With each change, it updates the URL parameters and submits the form to get the corresponding results.
 * It also handles the connection between the search form and the Google Maps.
 **/
export default class extends Controller {
  static values = { isAdmin: Boolean }
  static debounces = [
    { name: "submitForm", wait: 500 },
    { name: "submitFormWithDebounce", wait: 1000 },
  ]
  static outlets = ["navbar-search-form", "search-filter-bar"]

  async initialize() {
    this.submitForm()
    await google.maps.importLibrary("maps")
  }

  connect() {
    useDebounce(this)
    this.markers = []
    this.trackedProductList = []
    this.queryInfoMarker = null
    this.initMap()

    // Initialize search tracking
    this.trackedProductList = []
    this.trackedMapMarkerPinsList = []
    this.hasTrackedMapMoved = false

    this.fireTurboRequestOnPopState()
  }

  // Create map marker & infoWindow when the productInfoWindow is added to the DOM
  async productInfoWindowTargetConnected(target) {
    await google.maps.importLibrary("marker")
    this.addMarker(target)

    // Once all the productInfoWindows are added to the DOM, fit the map bounds
    if (this.markers.length == this.productInfoWindowTargets.length) {
      this.fitBounds()

      // if an infoWindow is already open and its product still present, update its content
      // else close the infoWindow
      if (this.infoBox) {
        const infoBoxNewMarker = this.markers.find(
          (marker) =>
            marker[0].position.lat == this.infoBox.position_.lat &&
            marker[0].position.lng == this.infoBox.position_.lng
        )

        if (infoBoxNewMarker) {
          const productInfoWindow = infoBoxNewMarker[1]
          this.infoBox.setContent(productInfoWindow.children[0].cloneNode(true))
        } else {
          this.infoBox.close()
          this.infoBox = null
        }
      }
    }
  }

  productListItemTargetConnected() {
    // Kill residual tooltips that may be left behind when hovering one while turbo request is in progress
    $(".tooltip").tooltip("hide")
  }

  // Remove map marker when the productInfoWindow is removed from the DOM
  async productInfoWindowTargetDisconnected(target) {
    await google.maps.importLibrary("marker")
    const markerContainer = this.markers.find((e) => e[1] == target)
    const marker = markerContainer[0]
    const index = this.markers.indexOf(markerContainer)
    marker.setMap(null)
    this.markers.splice(index, 1)
  }

  // Add event listeners to the search form inputs
  // When the user changes the search form inputs, push the form parameters to the URL and submit the form
  async formTargetConnected(target) {
    Array.from(target.elements).forEach((input) => {
      input.addEventListener("change", () => {
        this.pushFormParamsToUrl()
        this.submitFormFromModal()
      })
    })
  }

  async noResultsMessageTargetConnected() {
    this.fitBounds()
  }

  queryInfoTargetConnected() {
    this.setQueryInfoMarker()
  }

  queryInfoTargetDisconnected(target) {
    if (target.dataset.showMarker == "false") return

    if (this.queryInfoMarker) {
      this.queryInfoMarker.setMap(null)
    }
  }

  // Initialize the map
  async initMap() {
    const centerPoint = new google.maps.LatLng(
      this.centerPointTarget.dataset.lat,
      this.centerPointTarget.dataset.lng
    )
    var zoom = parseInt(this.formTarget.elements.zoom.value) || 7
    this.map = new google.maps.Map(this.mapTarget, this.mapOptions(centerPoint, zoom))
    this.addMapListeners()
  }

  async setQueryInfoMarker() {
    if (this.queryInfoTarget.dataset.showMarker == "false") return

    await google.maps.importLibrary("marker")
    this.queryInfoMarker = new google.maps.marker.AdvancedMarkerElement({
      map: this.map,
      position: {
        lat: parseFloat(this.queryInfoTarget.dataset.lat),
        lng: parseFloat(this.queryInfoTarget.dataset.lng),
      },
      content: markerIcon("pinned"),
    })
  }

  // Add event listeners to the map
  // dragend: when the map is dragged by the user, set the moved_map input to true
  // zoom_changed: when the map is zoomed by the user, set the moved_map input to true
  // idle: when the map stops moving, update the search form with the new bounds of the map and submit the form
  // mapMovedByUser: boolean to prevent the search form from submitting twice
  // boundsFitted: permit to ignore listeners when the map bounds are fitted
  // tracking Map Moved event in activity_search_controller.js and venue_search_controller.js
  addMapListeners() {
    this.mapMovedByUser = false
    this.boundsFitted = false

    google.maps.event.addListener(this.map, "click", () => {
      this.infoBox?.close()
    })

    google.maps.event.addListener(this.map, "dragend", () => {
      this.formTarget.elements.moved_map.value = true
      this.mapMovedByUser = true
    })

    google.maps.event.addListener(this.map, "zoom_changed", () => {
      this.formTarget.elements.zoom.value = this.map.getZoom()
      if (this.boundsFitted) return

      this.formTarget.elements.moved_map.value = true
      this.mapMovedByUser = true
    })
  }

  // Frame the map around the product markers on the page
  async fitBounds() {
    if (this.formTarget.elements.moved_map.value == "true") return

    const query = this.formTarget.elements.query.value
    const googlePlaceType = this.formTarget.elements.google_place_type.value
    const maxZoomOnFitBounds =
      query && googlePlaceType ? MAX_ZOOM_BY_QUERY_TYPE[googlePlaceType] : 9

    this.bounds = new google.maps.LatLngBounds()
    this.productInfoWindowTargets.forEach((productInfoWindow) => {
      this.bounds.extend({
        lat: parseFloat(productInfoWindow.dataset.lat),
        lng: parseFloat(productInfoWindow.dataset.lng),
      })
    })

    if (this.hasQueryInfoTarget) {
      this.bounds.extend({
        lat: parseFloat(this.queryInfoTarget.dataset.lat),
        lng: parseFloat(this.queryInfoTarget.dataset.lng),
      })
    }

    this.boundsFitted = true
    this.map.fitBounds(this.bounds)
    this.map.setZoom(Math.min(this.map.getZoom(), maxZoomOnFitBounds))
  }

  scrollResultsToTop() {
    this.productListTarget.scrollTo({ top: 0 }) // Desktop view
    window.scrollTo({ top: 0 }) // Mobile view
  }

  // Create a marker on the map for a productInfoWindow.
  // Adds an infoWindow to the marker that displays the productInfoWindow.
  // Changes the color of the marker when the productInfoWindow or the marker itself is hovered.
  addMarker(productInfoWindow) {
    const pinned = productInfoWindow.dataset.pinned == "true"
    const hasPromotionalOffer = productInfoWindow.dataset.hasPromotionalOffer == "true"
    const bookmarked = productInfoWindow.dataset.bookmarked == "true"

    let variant = "default"
    if (pinned) variant = "pinned"
    else if (hasPromotionalOffer) variant = "promotional"
    else if (bookmarked) variant = "bookmark"

    const marker = new google.maps.marker.AdvancedMarkerElement({
      map: this.map,
      position: {
        lat: parseFloat(productInfoWindow.dataset.lat),
        lng: parseFloat(productInfoWindow.dataset.lng),
      },
      content: markerIcon(variant),
      zIndex: pinned ? 2 : 1,
    })

    this.addInfoWindow(marker, productInfoWindow)
    this.markers.push([marker, productInfoWindow])

    marker.element.addEventListener("mouseenter", () => {
      this.toggleMarkerIconHovering(marker, true, variant)
    })

    marker.element.addEventListener("mouseleave", () => {
      this.toggleMarkerIconHovering(marker, false, variant)
    })

    productInfoWindow.previousElementSibling.addEventListener("mouseover", () => {
      this.toggleMarkerIconHovering(marker, true, variant)
    })

    productInfoWindow.previousElementSibling.addEventListener("mouseout", () => {
      this.toggleMarkerIconHovering(marker, false, variant)
    })

    // Once all the productInfoWindows have a marker, (re)initialize the clusterer
    if (this.markers.length == this.productInfoWindowTargets.length) {
      this.initMarkerClusterer()
    }
  }

  // // Add an infoWindow to a marker that displays a productInfoWindow
  // // Close the infoWindow if another marker is clicked
  // addInfoWindow(marker, productInfoWindow) {
  //   // define in activity_search_controller.js and venue_search_controller.js
  // }

  // Hook overridden by subclasses — pixel offset for the InfoBox popup
  // (used both for individual marker clicks and clustered marker clicks).
  // The first value centers the popup horizontally (-width/2), the second
  // shifts it vertically so it sits above the marker.
  infoBoxPixelOffset() {
    return new google.maps.Size(-100, -22)
  }

  // Hook overridden by subclasses — track an analytics event when a clustered pin is shown
  trackClusterMapPin(_targetMarkerInfoWindow) {
    // no-op by default
  }

  // Clustering: groups markers at the exact same lat/lng. Single-marker clusters
  // pass through unchanged (the individual marker is shown as-is).
  initMarkerClusterer() {
    if (this.clusterer) {
      this.clusterer.clearMarkers()
      this.clusterer.setMap(null)
    }

    const renderer = {
      render: ({ count, position }) => {
        const markerContent = this.clusterMarker(count)
        const targetMarkerInfoWindows = this.productInfoWindowTargets.filter((item) => {
          const lat = parseFloat(item.dataset.lat)
          const lng = parseFloat(item.dataset.lng)
          return lat === position.lat() && lng === position.lng()
        })
        targetMarkerInfoWindows.forEach((targetMarkerInfoWindow) => {
          const targetProductListItem = targetMarkerInfoWindow.previousElementSibling

          targetProductListItem.addEventListener("mouseover", () => {
            this.toggleClusterMarkerHovering(markerContent, true)
          })

          targetProductListItem.addEventListener("mouseout", () => {
            this.toggleClusterMarkerHovering(markerContent, false)
          })
        })

        return new google.maps.marker.AdvancedMarkerElement({
          position,
          content: markerContent,
        })
      }
    }

    this.clusterer = new MarkerClusterer({
      map: this.map,
      markers: this.markers.map(([marker]) => marker),
      renderer,
      onClusterClick: (event) => { event.preventDefault() },
      algorithm: new ExactLocationAlgorithm({ maxZoom: 22 }),
    })

    this.setClustersInfoWindows()
  }

  setClustersInfoWindows() {
    google.maps.event.addListener(this.clusterer, "click", (cluster) => {
      const shouldReopenInfoBox = (!this.infoBox || this.infoBox.position_ != cluster.marker.position)

      if (this.infoBox) {
        this.infoBox.close()
        this.infoBox = null
      }

      if (shouldReopenInfoBox) {
        this.infoBox = new InfoBox({
          alignBottom: true,
          pixelOffset: this.infoBoxPixelOffset(),
          infoBoxClearance: new google.maps.Size(10, 50),
          visible: true,
          closeBoxURL: "",
          zIndex: 10000,
        })

        this.infoBox.setPosition(cluster.marker.position)
        this.updateClusterInfoWindow(cluster, 0)
        this.infoBox.open(this.map)
      }
    })
  }

  updateClusterInfoWindow(cluster, markerIndex) {
    const markersInCluster = cluster.markers
    const targetMarker = markersInCluster[markerIndex]

    const targetMarkerInfoWindow = this.markers.find(([marker]) => marker == targetMarker)[1]
    const infoWindowContent = targetMarkerInfoWindow.children[0].cloneNode(true)

    const navigationContainer = document.createElement("div")
    navigationContainer.classList.add("d-flex", "justify-content-between", "align-items-center")

    const previousButton = document.createElement("button")
    previousButton.classList.add("btn", "btn-sm", "btn-outline-primary", "btn-icon__square-sm")
    previousButton.innerHTML = '<i class="fa-arrow-left fas"></i>'

    const nextButton = document.createElement("button")
    nextButton.classList.add("btn", "btn-sm", "btn-outline-primary", "btn-icon__square-sm")
    nextButton.innerHTML = '<i class="fa-arrow-right fas"></i>'

    const counterSpan = document.createElement("span")
    counterSpan.classList.add("font-size-sm", "text-muted")
    counterSpan.textContent = `${markerIndex + 1}/${cluster.markers.length}`

    navigationContainer.appendChild(previousButton)
    navigationContainer.appendChild(counterSpan)
    navigationContainer.appendChild(nextButton)

    previousButton.addEventListener("click", (e) => {
      e.preventDefault()
      e.stopPropagation()
      const newIndex = (markerIndex - 1 + markersInCluster.length) % markersInCluster.length
      this.updateClusterInfoWindow(cluster, newIndex)
    })

    nextButton.addEventListener("click", (e) => {
      e.preventDefault()
      e.stopPropagation()
      const newIndex = (markerIndex + 1) % markersInCluster.length
      this.updateClusterInfoWindow(cluster, newIndex)
    })

    infoWindowContent.children[0].appendChild(navigationContainer)
    this.infoBox.setContent(infoWindowContent)

    // Scroll the result list to the corresponding item and highlight it
    this.productListItemTargets.forEach((e) => e.children[0].classList.remove("pinned"))
    targetMarkerInfoWindow.previousElementSibling.children[0].classList.add("pinned")
    targetMarkerInfoWindow.previousElementSibling.scrollIntoView({
      behavior: "smooth",
      block: "center",
    })

    this.trackClusterMapPin(targetMarkerInfoWindow)
  }

  clusterMarker(count) {
    const container = document.createElement("div")

    container.style.display = "flex"
    container.style.alignItems = "center"
    container.style.justifyContent = "center"
    container.style.position = "relative"
    container.style.width = "24px"
    container.style.height = "24px"
    container.style.transform = "translateY(50%)"

    container.innerHTML = this.clusterMarkerIcon(count)

    container.addEventListener("mouseenter", () => {
      this.toggleClusterMarkerHovering(container, true)
    })

    container.addEventListener("mouseleave", () => {
      this.toggleClusterMarkerHovering(container, false)
    })

    return container
  }

  clusterMarkerIcon(count, color = PIN_COLORS["default"], radius = 12, fontSize = "12px") {
    return `
      <svg
        width="${radius * 2}" height="${radius * 2}" viewBox="0 0 ${radius * 2} ${radius * 2}" fill="none" xmlns="http://www.w3.org/2000/svg"
        style="position: absolute; filter: drop-shadow(0px 3px 4px rgba(0, 0, 0, 0.4));"
      >
        <circle cx="${radius}" cy="${radius}" r="${radius}" fill="white"/>
      </svg>

      <span style="
        position: relative;
        font-family: sans-serif;
        font-size: ${fontSize};
        font-weight: bold;
        color: ${color};
        z-index: 1;
      ">
        ${count}
      </span>
    `
  }

  toggleClusterMarkerHovering(marker, isHovered) {
    if (isHovered) {
      marker.children[1].style.color = PIN_COLORS["hover"]
      marker.children[1].style.fontSize = "14px"
      marker.children[0].style.width = "28px"
      marker.children[0].style.height = "28px"
      marker.style.zIndex = 3
    } else {
      marker.children[1].style.color = PIN_COLORS["default"]
      marker.children[1].style.fontSize = "12px"
      marker.children[0].style.width = "24px"
      marker.children[0].style.height = "24px"
      marker.style.zIndex = 1
    }
  }

  // Reset the address elements tracking when the map is moved
  resetFormElementAddress() {
    this.formTarget.elements.query.value = null
    this.formTarget.elements.address_establishment.value = null
    this.formTarget.elements.address_locality.value = null
    this.formTarget.elements.address_administrative_area_level_1.value = null
    this.formTarget.elements.address_administrative_area_level_2.value = null
    this.formTarget.elements.address_country.value = null
    this.formTarget.elements.address_zipcode.value = null
  }

  // Update the search form with the new bounds of the map and submit the form
  searchFromMap() {
    this.formTarget.elements.bounds_sw_lat.value = this.map.getBounds().getSouthWest().lat()
    this.formTarget.elements.bounds_sw_lng.value = this.map.getBounds().getSouthWest().lng()
    this.formTarget.elements.bounds_ne_lat.value = this.map.getBounds().getNorthEast().lat()
    this.formTarget.elements.bounds_ne_lng.value = this.map.getBounds().getNorthEast().lng()

    this.pushFormParamsToUrl()

    // prevents the form from submitting twice when fitBounds is called
    if (this.boundsFitted) {
      this.boundsFitted = false
    } else {
      this.submitForm()
    }
  }

  // Push the form parameters to the URL
  pushFormParamsToUrl() {
    const url = new URL(location)
    url.search = ""
    Array.from(this.formTarget.elements)
      .filter((e) => e.type != "submit")
      .forEach((e) => {
        const isRadioCheckbox = ["checkbox", "radio"].includes(e.type)
        const isValidRadioCheckbox = isRadioCheckbox && e.checked
        const isIgnoredHidden = e.type == "hidden" && e.value == "0" && !e.checked
        const shouldAppendParam =
          !isIgnoredHidden && (isValidRadioCheckbox || !isRadioCheckbox) && !!e.value
        const orderingByPrice =
          e.name == "order_by" && ["price_asc", "price_desc"].includes(e.value)

        // remove the order_by param if the user is ordering by price and the package filter is not selected
        if (orderingByPrice && !this.formTarget.elements.package.value) e.value = null

        if (shouldAppendParam) url.searchParams.append(e.name, e.value)
      })
    history.pushState({}, "", url)
  }

  submitForm() {
    this.formTarget.requestSubmit()
  }

  submitFormWithDebounce() {
    this.formTarget.requestSubmit()
  }

  submitFormFromModal() {
    this.isAdminValue ? this.submitFormWithDebounce() : this.submitForm()
  }

  changeOrderBy(e) {
    let orderBy = e.target.value
    this.formTarget.elements.order_by.value = orderBy
    this.pushFormParamsToUrl()
    this.submitForm()
  }

  // Ensure the modal body scrolls to the top when the modal is opened
  scrollModalBodyToTop() {
    setTimeout(() => {
      this.modalBodyTarget.scrollTo({ top: 0 })
    }, 200)
  }

  togglePackageFilter(e) {
    if (window.innerWidth < MEDIUM_BREAKPOINT) {
      this.mobileFilterButtonTarget.click()
    } else {
      this.packagesPopoverButtonTarget.click()
    }
    // track("Clicked on Page", { name: "Display total price"  })
  }

  // This allows to use the previous and next buttons of the browser.
  // The turbo request over a location.reload() permits to keep the map state.
  fireTurboRequestOnPopState() {
    window.addEventListener("popstate", function () {
      fetch(location.href, {
        method: "GET",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        },
      })
        .then((r) => r.text())
        .then((html) => Turbo.renderStreamMessage(html))
    })
  }

  toggleMarkerIconHovering(marker, isHovered, variant = "default") {
    const isSvgVariant = variant === "default" || variant === "pinned"
    const isPinned = variant === "pinned"
    const size = isHovered ? "28" : "24"

    marker.content.style.width = `${size}px`
    marker.content.style.height = `${size}px`
    marker.zIndex = isHovered ? 3 : (isPinned ? 2 : 1)

    if (isSvgVariant) {
      marker.content.setAttribute("width", size)
      marker.content.setAttribute("height", size)
      const innerCircle = marker.content.children[1]
      if (innerCircle) {
        innerCircle.style.fill = isHovered
          ? PIN_COLORS["hover"]
          : PIN_COLORS[isPinned ? "pinned" : "default"]
      }
    } else {
      const icon = marker.content.querySelector("i")
      if (icon) icon.style.fontSize = isHovered ? "14px" : "12px"
    }
  }

  // Configure the map options
  mapOptions(centerPoint, zoom = 7) {
    const optionHash = {
      center: centerPoint,
      zoom: zoom,
      mapId: "3fe2568bfd28a7e1",
      disableDefaultUI: true,
      zoomControl: true,
      mapTypeControl: false,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_TOP,
      },
    }

    return optionHash
  }

  isScrolledIntoView(el) {
    const rectElement = el.getBoundingClientRect()
    return rectElement.top >= 0 && rectElement.bottom <= window.innerHeight
  }
}
